import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button, Dialog } from '../../../design-system/ui';
import { Link } from '../../../components/Link';
import { useToast } from '../../../contexts/ToastContext';
import { usePlusEnabled } from '../plus/plus-flag';
import { SituationSheet } from './SituationSheet';
import { JOURNEY_NUDGE_EVENT, type JourneyNudgeDetail } from './journey-events';
import { reportActed, reportDismissed } from './journey-api';
import { journeyMessages as m } from './journey-messages';
import type { WireNudge } from '../../../types/journey';

/**
 * Mounted once under the toast provider. Listens for event-time nudges (APP-1 after the first
 * Applied, SET-2 after the first saved search, DEC-1 on an offer, WRK-1 on a hire...) and shows
 * them: a toast with the nudge's own button, or the full-screen moment for a hire. Also owns the
 * first-visit situation sheet. Renders nothing for anyone outside the Plus audience.
 */
export function JourneyBridge() {
  const plusEnabled = usePlusEnabled();
  if (!plusEnabled) return null;
  return (
    <>
      <ToastListener />
      <SituationSheet />
    </>
  );
}

function ToastListener() {
  const { showToast } = useToast();
  const intl = useIntl();
  const [fullscreen, setFullscreen] = useState<WireNudge | null>(null);

  useEffect(() => {
    const onNudge = (event: Event) => {
      const { nudge, surface } = (event as CustomEvent<JourneyNudgeDetail>).detail;
      if (surface === 'fullscreen') {
        setFullscreen(nudge);
        return;
      }
      showToast(
        nudge.title,
        'info',
        14000,
        {
          label: nudge.cta,
          onClick: () => {
            void reportActed(nudge);
            window.location.assign(nudge.url);
          },
        },
        nudge.body
      );
    };
    window.addEventListener(JOURNEY_NUDGE_EVENT, onNudge);
    return () => window.removeEventListener(JOURNEY_NUDGE_EVENT, onNudge);
  }, [showToast]);

  if (!fullscreen) return null;
  const close = () => {
    void reportDismissed(fullscreen);
    setFullscreen(null);
  };
  return (
    <Dialog
      open
      onClose={close}
      title={fullscreen.title}
      closeLabel={intl.formatMessage(m.congratsClose)}
      actions={
        <>
          <Button variant="tertiary" onClick={close}>
            {intl.formatMessage(m.congratsClose)}
          </Button>
          <Link
            href={fullscreen.url}
            onClick={() => {
              void reportActed(fullscreen);
              setFullscreen(null);
            }}
          >
            <span className="type-body font-semibold text-jc-accent underline underline-offset-4">{fullscreen.cta}</span>
          </Link>
        </>
      }
    >
      <p className="m-0 type-body text-jc-ink-secondary" data-testid="journey-fullscreen">
        {fullscreen.body}
      </p>
    </Dialog>
  );
}
